/**
 * @typedef {Object} OutputField
 * @property {string} name - Field identifier
 * @property {string|function} label - Display label or function returning label
 * @property {function} [checked] - Function determining if field is selected by default
 * @property {function} [visible] - Function determining if field should be shown
 * @property {boolean} [forceChecked] - If true, field is always selected and cannot be unchecked
 * @property {function} [disabled] - Function determining if fields should be disabled
 */

const populationTrackerOutputFields = [
  {
    name: "npi",
    label: ({ common }) =>
      common?.providerType === "physician" ? "Rendering NPI" : "Organization",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) => !!common?.outputs?.npi || !!common?.canBeVisualized
  },
  {
    name: "name",
    label: "Name",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.name || !!common?.canBeVisualized
  },
  {
    name: "classification",
    label: ({ common }) =>
      `${
        common?.providerType === "physician" ? "Physician" : "Organization"
      } Classification`,
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.classification || !!common?.canBeVisualized
  },
  {
    name: "specialization",
    label: ({ common }) =>
      `${
        common?.providerType === "physician" ? "Physician" : "Organization"
      } Specialization`,
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.specialization || !!common?.canBeVisualized
  },
  {
    name: "address",
    label: "Address",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.address || !!common?.canBeVisualized
  },
  {
    name: "city",
    label: "City",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.city || !!common?.canBeVisualized
  },
  {
    name: "stateName",
    label: "State",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.stateName || !!common?.canBeVisualized
  },
  {
    name: "zipCode",
    label: "ZIP Code",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.zipCode || !!common?.canBeVisualized
  },

  {
    name: "outputCode",
    label: ({ common }) => {
      switch (common?.codeType) {
        case "diag_code":
          return "ICD10-CM Code";
        case "procedurecode":
          return "CPT/HCPCS Code";
        default:
          return "Code";
      }
    },
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.outputCode || !!common?.canBeVisualized
  },

  {
    name: "codeServiceLine",
    label: "Code Service Line",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.codeServiceLine || !!common?.canBeVisualized
  },
  {
    name: "codeDescription",
    label: "Code Description",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.codeDescription || !!common?.canBeVisualized
  },

  {
    name: "distinctClaimsCount",
    label: "Count of Distinct Claims",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.distinctClaimsCount || !!common?.canBeVisualized
  },
  {
    name: "distinctPatientsCount",
    label: "Count of Distinct Patients",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.distinctPatientsCount || !!common?.canBeVisualized
  },
  {
    name: "sumCharges",
    label: "Sum of Charges",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.sumCharges || !!common?.canBeVisualized
  },

  {
    name: "placeOfService",
    label: "Place of Service",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.placeOfService || !!common?.canBeVisualized
  },
  // physician only
  {
    name: "locationNpi",
    label: "Location NPI",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.locationNpi || !!common?.canBeVisualized
  },
  {
    name: "locationName",
    label: "Location Name",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.locationName || !!common?.canBeVisualized
  },
  {
    name: "locationClassification",
    label: "Location Classification",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.locationClassification || !!common?.canBeVisualized
  },
  {
    name: "locationSpecialization",
    label: "Location Specialization",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.locationSpecialization || !!common?.canBeVisualized
  },
  {
    name: "billingNpi",
    label: "Billing NPI",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.billingNpi || !!common?.canBeVisualized
  },
  {
    name: "billingName",
    label: "Billing Name",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.billingName || !!common?.canBeVisualized
  },
  {
    name: "billingClassification",
    label: "Billing Classification",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.billingClassification || !!common?.canBeVisualized
  },
  {
    name: "billingSpecialization",
    label: "Billing Specialization",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.billingSpecialization || !!common?.canBeVisualized
  },
  {
    name: "hospitalAffiliationIds",
    label: "Hospital Affiliation Ids",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.hospitalAffiliationIds || !!common?.canBeVisualized
  },
  {
    name: "hospitalAffiliationNames",
    label: "Hospital Affiliation Names",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.hospitalAffiliationNames || !!common?.canBeVisualized
  },
  {
    name: "groupPracticeIds",
    label: "Group Practice Ids",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.groupPracticeIds || !!common?.canBeVisualized
  },
  {
    name: "groupPracticeNames",
    label: "Group Practice Names",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.groupPracticeNames || !!common?.canBeVisualized
  }
];

export default populationTrackerOutputFields;
